import axios from "axios";
import apiService from "./apiService";

const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api/digest`;

export const getSavedDigestService = async (title) => {
  const response = await apiService.get(`${API_BASE_URL}/saved_digest/${encodeURIComponent(title)}`);
  return response.data;
};

export const generateEmailDigestService = async ({ mailIntegrationId } = {}) => {
  try {
    const config = typeof mailIntegrationId === 'string' && mailIntegrationId.length > 0
      ? { params: { mailIntegrationId } }
      : undefined;
    const response = await apiService.get(`${API_BASE_URL}/generate_email_digest`, config);
    return response.data;
  } catch (error) {
    console.error("Error generating digest:", error);
    throw error;
  }
};

export const generateTaskDigestService = async (getToken, { startByDate, dueByDate }) => {
  try {
    const token = await getToken();
    const response = await axios.get(`${API_BASE_URL}/generate_task_digest/${startByDate}/${dueByDate}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error generating digest:", error);
    throw error;
  }
};

export const getDigestService = async (getToken, digestId) => {
  try {
    const token = await getToken();

    const response = await axios.get(`${API_BASE_URL}/get_digest/${digestId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error getting digest:", error);
    throw error;
  }
};

export const getAllDigestsService = async (getToken, userId) => {
  try {
    const token = await getToken();
    const response = await axios.get(
      `${API_BASE_URL}/get_all_digests/${userId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response;
  } catch (error) {
    console.error("Error getting all digests:", error);
    throw error;
  }
};

export const getDigestByTitleService = async (getToken, userId, title, mailIntegrationId) => {
  try {
    if (typeof mailIntegrationId !== 'string' || mailIntegrationId.length === 0) {
      throw new TypeError('Digest mailbox identity is required');
    }
    const token = await getToken();
    const query = new URLSearchParams({ mailIntegrationId });

    const response = await axios.get(
      `${API_BASE_URL}/get_digest_by_title/${userId}/${title}?${query}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting digest by title:", error);
    throw error;
  }
};

export const getDigestListService = async (userId) => {
  try {
    const response = await apiService.get(`${API_BASE_URL}/get_digest_list/${userId}`);
    return response.data;
  } catch (error) {
    console.error("Error getting digest list:", error);
    throw error;
  }
};

export const addDigestService = async (getToken, userId, digest) => {
  try {
    const token = await getToken();

    const response = await axios.post(
      `${API_BASE_URL}/add_digest`,
      { userId, digest },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding digest:", error);
    throw error;
  }
};

export const updateDigestService = async (getToken, digestId, digest) => {
  try {
    const token = await getToken();

    const response = await axios.post(
      `${API_BASE_URL}/update_digest`,
      { digestId, digest },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating digest:", error);
    throw error;
  }
};

export const getDigestEmailsService = async (getToken, digestId) => {
  try {
    const token = await getToken();

    const response = await axios.get(
      `${API_BASE_URL}/get_digest_emails/${digestId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting digest emails:", error);
    throw error;
  }
};

export const addDigestEmailsService = async (getToken, digestId, emails) => {
  try {
    const token = await getToken();

    const response = await axios.post(
      `${API_BASE_URL}/add_digest_emails`,
      { digestId, emails },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding digest emails:", error);
    throw error;
  }
};

export const updateDigestEmailsService = async (getToken, digestId, emails) => {
  try {
    const token = await getToken();

    const response = await axios.post(
      `${API_BASE_URL}/update_digest_emails`,
      { digestId, emails },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error updating digest emails:", error);
    throw error;
  }
};

const normalizeEmailRequest = (emailRequest, legacyFields = {}) => (
  typeof emailRequest === "string"
    ? { emailId: emailRequest, ...legacyFields }
    : emailRequest
);

const withMailIntegrationQuery = (path, mailIntegrationId) => (
  typeof mailIntegrationId === "string" && mailIntegrationId.length > 0
    ? `${path}?mailIntegrationId=${encodeURIComponent(mailIntegrationId)}`
    : path
);

export const getEmailActionsService = async (getToken, emailRequest) => {
  try {
    const token = await getToken();
    const { emailId, mailIntegrationId } = normalizeEmailRequest(emailRequest);

    const response = await axios.get(
      withMailIntegrationQuery(
        `${API_BASE_URL}/get_email_actions/${encodeURIComponent(emailId)}`,
        mailIntegrationId
      ),
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error getting email actions:", error);
    throw error;
  }
};

// TODO: Pass the getToken function to the service
export const addEmailActionService = async (
  getToken,
  emailRequest,
  legacyEmailActions
) => {
  try {
    const token = await getToken();
    const { emailId, emailActions, mailIntegrationId } = normalizeEmailRequest(
      emailRequest,
      { emailActions: legacyEmailActions }
    );

    const response = await axios.post(
      `${API_BASE_URL}/add_email_action`,
      { emailId, emailActions, ...(mailIntegrationId ? { mailIntegrationId } : {}) },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding email action:", error);
    throw error;
  }
};

export const deleteEmailActionByTypeService = async (getToken, emailRequest, legacyEmailActionType) => {
  try {
    const token = await getToken();
    const { emailId, emailActionType, mailIntegrationId } = normalizeEmailRequest(
      emailRequest,
      { emailActionType: legacyEmailActionType }
    );
    const response = await axios.delete(withMailIntegrationQuery(
      `${API_BASE_URL}/delete_email_action_by_type/${encodeURIComponent(emailId)}/${encodeURIComponent(emailActionType)}`,
      mailIntegrationId
    ), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error deleting email action:", error);
    throw error;
  }
};

// TODO: Pass the getToken function to the service
export const addEmailFileIdService = async (
  getToken,
  emailRequest,
  legacyChatId,
  legacyFileId
) => {
  try {
    const token = await getToken();
    const { emailId, chatId, fileId, mailIntegrationId } = normalizeEmailRequest(
      emailRequest,
      { chatId: legacyChatId, fileId: legacyFileId }
    );

    const response = await axios.post(
      `${API_BASE_URL}/add_email_file_id`,
      { emailId, chatId, fileId, ...(mailIntegrationId ? { mailIntegrationId } : {}) },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Error adding email file id:", error);
    throw error;
  }
};

export const deleteEmailFileIdService = async (getToken, emailRequest, legacyFileId) => {
  try {
    const token = await getToken();
    const { emailId, fileId, mailIntegrationId } = normalizeEmailRequest(
      emailRequest,
      { fileId: legacyFileId }
    );

    const response = await axios.delete(withMailIntegrationQuery(
      `${API_BASE_URL}/delete_email_file_id/${encodeURIComponent(emailId)}/${encodeURIComponent(fileId)}`,
      mailIntegrationId
    ), {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error deleting email file id:', error);
    throw error;
  }
}
